import { useEffect, useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { Link, NavLink } from 'react-router-dom'

const menuItems = [
  {
    to: '/',
    label: 'Home',
    eyebrow: 'Studio',
    description: 'Return to the Zpace Studio home page.',
    end: true,
  },
  {
    to: '/#explore',
    label: 'Explore',
    eyebrow: 'Destinations',
    description: 'Browse public projects, games, and member tools.',
  },
  {
    to: '/about/',
    label: 'About',
    eyebrow: 'Studio profile',
    description: 'Focus, tools, interests, and contact information.',
  },
  {
    to: '/privacy/',
    label: 'Privacy',
    eyebrow: 'Policy library',
    description: 'Privacy information for current sites and applications.',
  },
]

export default function MobileMenu({ open, onClose }) {
  const rootRef = useRef(null)
  const scrimRef = useRef(null)
  const timelineRef = useRef(null)
  const openMenuRef = useRef(null)
  const closeMenuRef = useRef(null)

  useGSAP(
    () => {
      const root = rootRef.current
      const scrim = scrimRef.current
      const panels = gsap.utils.toArray('.mobile-menu-card', root)
      const copies = gsap.utils.toArray('.mobile-menu-card__copy', root)

      gsap.set(root, { autoAlpha: 0, pointerEvents: 'none' })
      gsap.set(scrim, { autoAlpha: 0 })
      gsap.set(panels, { xPercent: 108, autoAlpha: 1, force3D: true })
      gsap.set(copies, { autoAlpha: 0, y: 10, force3D: true })

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        openMenuRef.current = () => {
          gsap.set(root, { autoAlpha: 1, pointerEvents: 'auto' })
          gsap.set(scrim, { autoAlpha: 1 })
          gsap.set(panels, { xPercent: 0, x: 0, y: 0, rotate: 0, autoAlpha: 1 })
          gsap.set(copies, { autoAlpha: 1, y: 0 })
        }
        closeMenuRef.current = () => {
          gsap.set(root, { autoAlpha: 0, pointerEvents: 'none' })
          gsap.set(scrim, { autoAlpha: 0 })
        }
        return
      }

      openMenuRef.current = () => {
        gsap.set(root, { autoAlpha: 1, pointerEvents: 'auto' })
        gsap.set(panels, { xPercent: 108, x: 0, y: 0, rotate: 0, autoAlpha: 1 })
        gsap.set(copies, { autoAlpha: 0, y: 10 })

        timelineRef.current = gsap
          .timeline({ defaults: { ease: 'power3.out' } })
          .to(scrim, { autoAlpha: 1, duration: 0.2, ease: 'power2.out' }, 0)
          .to(
            panels,
            { xPercent: 0, duration: 0.46, stagger: 0.045, ease: 'power2.out' },
            0,
          )
          .to(
            copies,
            { autoAlpha: 1, y: 0, duration: 0.25, stagger: 0.03, ease: 'power1.out' },
            0.07,
          )
      }

      closeMenuRef.current = () => {
        timelineRef.current = gsap
          .timeline({
            defaults: { ease: 'power3.in' },
            onComplete: () => {
              gsap.set(root, { autoAlpha: 0, pointerEvents: 'none' })
              gsap.set(panels, { xPercent: 108, x: 0, y: 0, rotate: 0, autoAlpha: 1 })
              gsap.set(copies, { autoAlpha: 0, y: 10 })
              gsap.set(scrim, { autoAlpha: 0 })
            },
          })
          .to(
            panels,
            {
              x: 26,
              y: '108vh',
              rotate: 'random(-8, 8)',
              autoAlpha: 0,
              duration: 0.48,
              stagger: { from: 'end', each: 0.025 },
            },
            0.02,
          )
          .to(scrim, { autoAlpha: 0, duration: 0.22, ease: 'power2.in' }, 0.08)
      }
    },
    { scope: rootRef },
  )

  useEffect(() => {
    timelineRef.current?.kill()

    if (open) {
      openMenuRef.current?.()
    } else {
      closeMenuRef.current?.()
    }
  }, [open])

  return (
    <div
      id="mobile-menu"
      ref={rootRef}
      className={`mobile-menu${open ? ' is-open' : ''}`}
      aria-hidden={!open}
    >
      <button
        ref={scrimRef}
        className="mobile-menu__scrim"
        type="button"
        aria-label="Close navigation menu"
        onClick={onClose}
      />

      <nav className="mobile-menu__stack" aria-label="Mobile navigation">
        {menuItems.map((item) => {
          const MenuLink = item.to.includes('#') ? Link : NavLink

          return (
            <MenuLink
              key={item.to}
              className="mobile-menu-card"
              end={item.end}
              tabIndex={open ? undefined : -1}
              to={item.to}
              onClick={onClose}
            >
              <span className="mobile-menu-card__copy">
                <span className="mobile-menu-card__eyebrow">{item.eyebrow}</span>
                <strong>{item.label}</strong>
                <span>{item.description}</span>
              </span>
            </MenuLink>
          )
        })}

        <a
          className="mobile-menu-card mobile-menu-card--dashboard"
          href="https://accounts.zpacestudio.com/"
          tabIndex={open ? undefined : -1}
          onClick={onClose}
        >
          <span className="mobile-menu-card__copy">
            <span className="mobile-menu-card__eyebrow">Private workspace</span>
            <strong>Dashboard</strong>
            <span>Sign in to view the applications available to your account.</span>
          </span>
        </a>
      </nav>
    </div>
  )
}
